import AccountStore from "../stores/AccountStore"
import AuthenticationStore from "../stores/AuthenticationStore"
import { ELoggedInUserType, IUserDocument, IUserRole, IAccountDocument, ISupervisor } from "../models"
import _ from "lodash"

export function getAllPermissions(account: IAccountDocument) {
	const allPermissions = []
	const initPermissions = getPermissionsObject(account)
	Object.keys(initPermissions).forEach((key) => {
		Object.keys(initPermissions[key]).forEach((subKey) => {
			allPermissions.push(initPermissions[key][subKey])
		})
	})
	return allPermissions
}

export function getAllPermissionsForAccountOwnerRole(account: IAccountDocument) {
	const allPermissions = getAllPermissions(account)
	return allPermissions.filter(item => item !== AuthenticationStore.Permissions.Employees.AllowSelfEmpEdit)
}

export function getAccountOwnerRoles(account: IAccountDocument) {
	return account.AccountSettings.UserRoles.filter(role =>
		!getAllPermissionsForAccountOwnerRole(account).find(expectedPermission => !role.Permissions.includes(expectedPermission))
	)
}

export function getFeaturePermissions(featurePermissionName) {
	const permissions = []
	Object.keys(AuthenticationStore.Permissions[featurePermissionName]).forEach((key) =>
		permissions.push(AuthenticationStore.Permissions[featurePermissionName][key])
	)
	return permissions
}

export function getPermissionsObject(account: IAccountDocument) {
	const initPermissions = _.cloneDeep(AuthenticationStore.Permissions)
	if (!account.AccountSettings.FeatureSettings.EnableExpenses) {
		delete initPermissions.Import.ImportExpenseTimeCSV
		delete initPermissions.Import.ImportExpenseAmountCSV
		delete initPermissions.Expense
	}
	account.AccountSettings.HierarchySettings.DisableHierarchyItemsForAccountUserUnlessAssigned && delete initPermissions.Account.ViewFullHierarchy
	!account.AccountSettings.FeatureSettings.EnableTimecards && delete initPermissions.Timecards
	!account.AccountSettings.FeatureSettings.EnablePayrollWorkflow && delete initPermissions.Payroll
	if (account.AccountSettings.FeatureSettings.EnableExpenses && !account.AccountSettings.FeatureSettings.EnablePayrollWorkflow) {
		delete initPermissions.Expense.ViewPayrollExpense
		delete initPermissions.Expense.LockPayrollExpense
		delete initPermissions.Expense.ClosePayrollExpense
		delete initPermissions.Expense.ExportPayrollExpense
	}
	!account.AccountSettings.FeatureSettings.EnableEquipment && delete initPermissions.Equipment
	!account.AccountSettings.FeatureSettings.EnableAccrualsTimeoff && delete initPermissions.Accrual
	!account.AccountSettings.FeatureSettings.EnableAccrualsTimeoff && delete initPermissions.LeaveTracking
	!account.AccountSettings.FeatureSettings.EnableSchedule && delete initPermissions.Schedule
	account.AccountSettings.FeatureSettings.EnablePayrollWorkflow && !account.AccountSettings.FeatureSettings.EnableJobs && delete initPermissions.Payroll.ViewPayrollByJob
	account.AccountSettings.FeatureSettings.EnablePayrollWorkflow && !account.AccountSettings.FeatureSettings.EnableAccrualsTimeoff && delete initPermissions.Payroll.ClosePayroll
	account.AccountSettings.FeatureSettings.EnablePayrollWorkflow && account.AccountSettings.FeatureSettings.EnableExpenses && !account.AccountSettings.FeatureSettings.EnableAccrualsTimeoff && delete initPermissions.Expense.ClosePayrollExpense
	!account.AccountSettings.FeatureSettings.EnableSupervisorDirectReport && delete initPermissions.SupervisorDirectReport
	!account.AccountSettings.FeatureSettings.EnableSupervisorDirectReport && delete initPermissions.Employees.CanAddNonDirectReportEmployees

	if (account.AccountSettings.FeatureSettings.EnableTimecards && !account.AccountSettings.FeatureSettings.EnablePriorPeriodAdjustments) {
		delete initPermissions.Timecards.PriorPeriodAdjustments
		delete initPermissions.Timecards.PriorPeriodAdjustmentsExportEdits
	}
	!account.AccountSettings.FeatureSettings.EnableHR && delete initPermissions.Rewards
	!account.AccountSettings.FeatureSettings.EnableJobs && delete initPermissions.Import.ImportJobsCSV
	!account.AccountSettings.FeatureSettings.EnableSchedule && delete initPermissions.Import.ImportScheduleCSV
	if (!account.AccountSettings.FeatureSettings.EnableJobs) {
		delete initPermissions.Jobs
		delete initPermissions.JobTabs
		delete initPermissions.Devices.ViewJobDevices
		delete initPermissions.Devices.AddJobDevices
		delete initPermissions.Devices.EditJobDevices
		delete initPermissions.Devices.DeleteJobDevices
	}
	!account.AccountSettings.FeatureSettings.EnableSupLock && !!initPermissions.Payroll && delete initPermissions.Payroll.EditPayrollAfterLock
	!account.AccountSettings.FeatureSettings.EnableATS && delete initPermissions.HRAccessATS
	!account.AccountSettings.FeatureSettings.EnablePunchRule && delete initPermissions.Timecards.PunchRuleOverride
	!account.AccountSettings.FeatureSettings.EnableMessageCenter && delete initPermissions.HRAccessMessageCenter
	!account.AccountSettings.FeatureSettings.EnableMessageCenter && delete initPermissions.HRAccessCampaigns
	return initPermissions
}

export function isAccountAdmin(roleId = null) {
	if (!AuthenticationStore.getLoggedInUser() || !AccountStore.getCurrentAccount()) return false
	const userRoleId = roleId ? roleId : AuthenticationStore.getLoggedInUser().UserRoleId
	const userRole = AccountStore.getCurrentAccountSettings().UserRoles.find((item) => item.Id === userRoleId)
	return userRole ? userRole.Permissions.includes(AuthenticationStore.Permissions.Account.AccountAdmin) : false
}

export function existAccountAdminInAccount() {
	const accountAdminRoles = AccountStore.getCurrentAccountSettings().UserRoles.filter((item) => item.Permissions.includes(AuthenticationStore.Permissions.Account.AccountAdmin))
	if (accountAdminRoles.length === 0) {
		return false
	} else {
		const accountUsers = AccountStore.getUsers()
		const accountAdminUser = accountUsers.filter((user) => {
			return accountAdminRoles.find((role) => role.Id === user.UserRoleId) ? true : false
		})
		return accountAdminUser ? true : false
	}
}

export function getUserRoleById(userRoleId) {
	return AccountStore.getCurrentAccountSettings().UserRoles.find((item) => item.Id === userRoleId)
}

export function getLoggedInUserType(): ELoggedInUserType {
	const loggedInUser = AuthenticationStore.getLoggedInUser() as IUserDocument
	if (!loggedInUser) return null
	if (AuthenticationStore.getIsAccountManager()) return ELoggedInUserType.AccountManager
	if (loggedInUser.AccountUser && loggedInUser.IsEmployee) return ELoggedInUserType.EsAccUser
	if (loggedInUser.IsEmployee) return ELoggedInUserType.Employee
	if (loggedInUser.AccountUser) return isAccountAdmin(loggedInUser.UserRoleId) ? ELoggedInUserType.AccountAdmin : ELoggedInUserType.AccountUser
}

export function isAccountManager() {
	const loggedInUser = AuthenticationStore.getLoggedInUser()
	return loggedInUser.UserRole && loggedInUser.UserRole.Permissions.includes(AuthenticationStore.SpecialPermission.AccountManager)
}

export function isGlobalAdmin() {
	const loggedInUser = AuthenticationStore.getLoggedInUser()
	return loggedInUser.UserRole && loggedInUser.UserRole.Permissions.includes(AuthenticationStore.SpecialPermission.GlobalAdmin)
}

export function hasPermission(permission, supervisor: ISupervisor = null): boolean {
	if ((!supervisor && !AuthenticationStore.getLoggedInUser()) || !AccountStore.getCurrentAccount()) return false
	const userRoleId = supervisor ? supervisor.UserRoleId : AuthenticationStore.getLoggedInUser().UserRoleId
	if (!userRoleId) return false
	const userRole = AccountStore.getCurrentAccount().AccountSettings.UserRoles.find((item) => item.Id === userRoleId)
	if (!userRole) return false
	return userRole.Permissions.includes(permission)
}

export function loggedInUserHasSpecificPermission(permission) {
	return hasSpecificPermission(permission, AuthenticationStore.getLoggedInUser())
}

export function hasSpecificPermission(permission, employee: IUserDocument) {
	return employee && employee.UserRole && employee.UserRole.Permissions ?
		employee.UserRole.Permissions.includes(permission) :
		false
}

export function allowedImportPermissions() {
	const initPermissions = getPermissionsObject(AccountStore.getCurrentAccount())

	const importPermissions = Object.keys(initPermissions.Import)

	let allowedImportPermissions = []
	importPermissions.forEach(key =>
		hasPermission(initPermissions.Import[key]) &&
		allowedImportPermissions.push(initPermissions.Import[key])
	)
	return allowedImportPermissions
}

export function getSpecificUserRole(originalUserRole: IUserRole, permission: string, value: boolean): IUserRole {
	let userRole = _.cloneDeep(originalUserRole)
	if (value) {
		if (!userRole) {
			userRole = {
				Type: ELoggedInUserType.Employee,
				Permissions: []
			}
		}
		!userRole.Permissions.includes(permission) &&
			userRole.Permissions.push(permission)
	}
	else {
		if (userRole) {
			userRole.Permissions = userRole.Permissions.filter(i => i !== permission)
		}
		if (userRole && userRole.Permissions.length === 0) {
			userRole = null
		}
	}
	return userRole
}

export function getAllAccountManagerPermissions() {
	const allPermissions = []
	const accountManagerPermissions = AuthenticationStore.AccountManagerUserPermissions
	Object.keys(accountManagerPermissions).forEach((key) => {
		Object.keys(accountManagerPermissions[key]).forEach((subKey) => {
			allPermissions.push(accountManagerPermissions[key][subKey])
		})
	})
	return allPermissions
}

export function hasAccountManagerPermission(permission): boolean {
	return AuthenticationStore.getLoggedInAccountManagerUser() &&
		AuthenticationStore.getLoggedInAccountManagerUser().UserRole &&
		AuthenticationStore.getLoggedInAccountManagerUser().UserRole.Permissions.includes(permission)
}

export function getAllowedExpensePayTypeIds(): string[] {
	var allPayTypeIds = AccountStore.getCurrentAccount().PayTypes.map(i => i.Id)
	if (getLoggedInUserType() !== ELoggedInUserType.Employee) {
		const userRole = getUserRoleById(AuthenticationStore.getLoggedInUser().UserRoleId)
		return (!userRole.ExpensePayTypes || !userRole.ExpensePayTypes.length) ? allPayTypeIds : userRole.ExpensePayTypes
	}
	else {
		return allPayTypeIds
	}
}